// widgetService.js
// This service allows global control of the chat widget

// Stores the widget reference for global use
let widgetRef = null;

/**
 * Sets the widget reference for external control
 * @param {Object} ref - React reference to the Widget component
 */
export const setWidgetRef = (ref) => {
  widgetRef = ref;
};

/**
 * Opens the chat widget
 * @returns {boolean} - true if the widget was opened, false if the reference is not available
 */
export const openWidget = () => {
  if (widgetRef) {
    widgetRef.open();
    return true;
  }
  return false;
};

/**
 * Closes the chat widget
 * @returns {boolean} - true if the widget was closed, false if the reference is not available
 */
export const closeWidget = () => {
  if (widgetRef) {
    widgetRef.close();
    return true;
  }
  return false;
};

/**
 * Toggles the visibility of the chat widget
 * @returns {boolean} - true if the widget was toggled, false if the reference is not available
 */
export const toggleWidget = () => {
  if (widgetRef) {
    widgetRef.toggle();
    return true;
  }
  return false;
};

/**
 * Checks if the widget is open
 * @returns {boolean} - true if the widget is open, false if closed or if the reference is not available
 */
export const isWidgetOpen = () => {
  if (widgetRef) {
    const isOpen = widgetRef.isOpen();
    return isOpen;
  }
  return false;
};

// Creates a button to open the widget and adds it to any element on the page
export const createWidgetButton = (options = {}) => {
  const {
    targetSelector = "body", // Where the button will be added
    buttonText = "Chat",
    position = "right", // 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right' or 'custom'
    customPosition = {}, // For custom position: { top, right, bottom, left }
    buttonStyle = {}, // Additional CSS styles for the button
    buttonClass = "", // Additional classes for the button
    iconOnly = false, // If true, shows only the icon without text
  } = options;

  const targetElement = document.querySelector(targetSelector);
  if (!targetElement) {
    return null;
  }

  // Create button
  const button = document.createElement("button");
  button.className = `btimeai-widget-button ${buttonClass}`;

  // Add positioning to the button
  let positionStyles = {};
  if (position === "left") {
    positionStyles = { position: "fixed", left: "20px", bottom: "20px" };
  } else if (position === "right") {
    positionStyles = { position: "fixed", right: "20px", bottom: "20px" };
  } else if (position === "top-left") {
    positionStyles = { position: "fixed", left: "20px", top: "20px" };
  } else if (position === "top-right") {
    positionStyles = { position: "fixed", right: "20px", top: "20px" };
  } else if (position === "bottom-left") {
    positionStyles = { position: "fixed", left: "20px", bottom: "20px" };
  } else if (position === "bottom-right") {
    positionStyles = { position: "fixed", right: "20px", bottom: "20px" };
  } else if (position === "custom") {
    positionStyles = { position: "fixed", ...customPosition };
  }

  // Define base button style
  const baseStyles = {
    display: "flex",
    alignItems: "center",
    padding: iconOnly ? "10px" : "10px 16px",
    backgroundColor: "#9728FD",
    borderRadius: iconOnly ? "50%" : "30px",
    color: "white",
    border: "none",
    boxShadow: "0 2px 10px rgba(151, 40, 253, 0.3)",
    cursor: "pointer",
    zIndex: 999,
    ...positionStyles,
    ...buttonStyle,
  };

  // Apply styles to the button
  Object.assign(button.style, baseStyles);

  // Add icon and text
  button.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: ${
      iconOnly ? "0" : "8px"
    }">
      <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" 
        stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    ${iconOnly ? "" : `<span>${buttonText}</span>`}
  `;

  // Add click event to open the widget
  button.addEventListener("click", () => {
    toggleWidget();
  });

  // Add the button to the target element
  targetElement.appendChild(button);

  return button;
};

export default {
  setWidgetRef,
  openWidget,
  closeWidget,
  toggleWidget,
  isWidgetOpen,
  createWidgetButton,
};
